"use strict";
/* eslint-disable no-underscore-dangle */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DataProxy = void 0;
const tiny_typed_emitter_1 = require("tiny-typed-emitter");
const get_1 = __importDefault(require("../utils/get"));
const pluginHost_1 = require("./pluginHost");
const immer_1 = require("@lyonbot/immer");
const immerPatchConvertor_1 = require("../utils/immerPatchConvertor");
const resolveConnectTarget_1 = require("./resolveConnectTarget");
class DataProxy extends tiny_typed_emitter_1.TypedEmitter {
    constructor(dataMapper) {
        var _a;
        super();
        this.isConnected = false;
        /** 用于 pathChanged 事件 */
        this.oldPath = false;
        this.path = false;
        this.onNodeUpdated = (node) => {
            if (node !== this.node)
                return;
            const { relativePathPartsToNode } = this;
            const oldSrc = this.src;
            const src = relativePathPartsToNode ? (0, get_1.default)(node.src, relativePathPartsToNode) : node.src;
            if (oldSrc === src)
                return; // not changed
            this.oldSrc = oldSrc;
            this.src = src;
            this.reinitializeDraft();
            this.emit('updated', this);
        };
        this.onNodePathChanged = (node) => {
            if (node !== this.node)
                return;
            this.oldPath = this.path;
            this.path = node.path + (this.relativePathToNode || '');
            this.emit('pathChanged', this);
        };
        this.onNodeDestroyed = (node) => {
            if (node !== this.node)
                return;
            this.disconnect();
        };
        this._draftSubmitting = false;
        this.dataMapper = dataMapper;
        (0, immer_1.enablePatches)();
        (_a = pluginHost_1.pluginHost.dataProxy.initialize) === null || _a === void 0 ? void 0 : _a.call(this, this);
    }
    disconnect() {
        if (!this.isConnected)
            return;
        this.finalizeCurrentDraft(true);
        this.emit('beforeDisconnect', this);
        this.isConnected = false;
        const { node } = this;
        if (node) {
            node.off('updated', this.onNodeUpdated);
            node.off('pathChanged', this.onNodePathChanged);
            node.off('destroyed', this.onNodeDestroyed);
        }
        this.node = undefined;
        this.relativePathToNode = undefined;
        this.path = false;
        this.oldPath = false;
        this.src = undefined;
        this.oldSrc = undefined;
    }
    dispose() {
        this.disconnect();
        this.removeAllListeners();
    }
    connect(target) {
        const { dataMapper } = this;
        const { node, relativePathPartsToNode, relativePathToNode } = (0, resolveConnectTarget_1.resolveConnectTarget)(dataMapper, target);
        const path = node.path + (relativePathToNode || '');
        this.disconnect();
        this.node = node;
        this.relativePathPartsToNode = relativePathPartsToNode;
        this.relativePathToNode = relativePathToNode;
        this.path = path;
        this.oldPath = path;
        node.on('updated', this.onNodeUpdated);
        node.on('pathChanged', this.onNodePathChanged);
        node.on('destroyed', this.onNodeDestroyed);
        const src = relativePathPartsToNode ? (0, get_1.default)(node.src, relativePathPartsToNode) : node.src;
        this.oldSrc = src;
        this.src = src;
        this.isConnected = true;
        this.reinitializeDraft();
        this.emit('connected', this);
    }
    /**
     * note: if there is already a draft, it will be discarded!
     */
    reinitializeDraft() {
        this.finalizeCurrentDraft(false);
        if (!this.isConnected)
            return;
        const draft = (0, immer_1.createDraft)({ value: this.src });
        (0, immer_1.onceDraftModified)(draft, () => this.queueDraftSubmition());
        this._currentDraft = draft;
    }
    finalizeCurrentDraft(submitChanges) {
        const draft = this._currentDraft;
        this._currentDraft = void 0;
        this._draftSubmitting = false;
        if (!draft || !(0, immer_1.isDraft)(draft))
            return;
        if (!this.isConnected)
            return;
        if (!submitChanges) {
            (0, immer_1.finishDraft)(draft); // seal the original draft
            return;
        }
        const basePath = this.path;
        const finalPatches = [];
        const result = (0, immer_1.finishDraft)(draft, (patches, inversePatches) => {
            patches.forEach((it) => {
                const cvted = (0, immerPatchConvertor_1.fromImmerPatch)(it);
                cvted.path = basePath + cvted.path.substr(6); // 6 = length of '/value'
                finalPatches.push(cvted);
            });
        });
        if (result.value === this.src)
            return;
        // note: might change the array
        this.emit('beforeCommit', this, finalPatches);
        if (finalPatches.length > 0) {
            this.dataMapper.applyPatches(finalPatches);
        }
    }
    get value() {
        var _a;
        const { isConnected, path } = this;
        if (!isConnected || typeof path !== 'string')
            return;
        return (_a = this._currentDraft) === null || _a === void 0 ? void 0 : _a.value;
    }
    set value(value) {
        const { isConnected, path } = this;
        if (!isConnected || typeof path !== 'string')
            return;
        this.dataMapper.applyPatches([
            (value === undefined)
                ? { op: 'delete', path }
                : { op: 'set', path, value },
        ]);
    }
    async queueDraftSubmition() {
        if (this._draftSubmitting)
            return;
        const promise = Promise.resolve().then(() => {
            if (this._draftSubmitting !== promise)
                return;
            this._draftSubmitting = false;
            this.finalizeCurrentDraft(true);
        });
        this._draftSubmitting = promise;
        await promise;
    }
    async waitUntilDraftSubmitted() {
        await this._draftSubmitting;
    }
}
exports.DataProxy = DataProxy;
